import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { RootState } from "store";
import { setOrderDuration } from "store/slices/tradingSlice";
import { clampDuration } from "shared/utils";
import { MAX_DURATION_SECONDS } from "./constants";
import { getStoredDuration, setStoredDuration } from "./timeSessionStorage";

type TimeField = "hours" | "minutes" | "seconds";

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value));

/**
 * Длительность сделки живёт в redux (orderDuration): при смене пары (t=10)
 * и обновлении tier (t=8) слайс сам подгоняет её под новые min/max.
 */
export const useDuration = () => {
  const dispatch = useDispatch();
  const userId = useSelector((state: RootState) => state.auth.user?.id);
  const duration = useSelector((state: RootState) => state.trading.orderDuration);
  const limits = useSelector((state: RootState) => state.trading.expirationLimits);
  const step = limits.expiration_step_sec;

  const bound = (total: number) =>
    Math.min(clampDuration(total, limits), MAX_DURATION_SECONDS);

  // Восстанавливаем выбор из sessionStorage — им пользуются Chart.tsx и режим
  // Switch Time, а не только этот хук.
  useEffect(() => {
    const stored = userId != null ? getStoredDuration(userId) : null;
    if (stored != null) dispatch(setOrderDuration(bound(stored)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const hours = Math.floor(duration / 3600);
  const minutes = Math.floor((duration % 3600) / 60);
  const seconds = duration % 60;

  const setDuration = (total: number) => {
    const bounded = bound(total);
    dispatch(setOrderDuration(bounded));
    if (userId != null) setStoredDuration(userId, bounded);
  };

  // Секунды шагают на expiration_step_sec (а не на 1) — переполнение само
  // уходит в минуты, т.к. это просто ±step к общей длительности.
  const adjustField = (field: TimeField, delta: number) => {
    if (field === "seconds") {
      setDuration(duration + delta * step);
      return;
    }
    if (field === "minutes") {
      setDuration(hours * 3600 + clamp(minutes + delta, 0, 59) * 60 + seconds);
      return;
    }
    setDuration(clamp(hours + delta, 0, 23) * 3600 + minutes * 60 + seconds);
  };

  // Кнопки +/- у поля Time шагают на expiration_step_sec по умолчанию, либо
  // на переданный шаг — см. MINUTE_STEP для режима Switch Time.
  const adjustDuration = (delta: number, stepSeconds: number = step) =>
    setDuration(duration + delta * stepSeconds);

  return {
    duration,
    hours,
    minutes,
    seconds,
    adjustDuration,
    adjustField,
    setDuration,
  };
};
